import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Copy,
  RotateCw,
  Eraser,
  GraduationCap,
  BookOpen,
  Loader2,
  Info,
  User,
  Settings2,
  X,
  CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

import TagButton from "@/components/TagButton";
import { CustomTagAdder, CustomTagButton } from "@/components/CustomTags";
import LanguageLevelToggle from "@/components/LanguageLevelToggle";
import TeacherProfileSheet from "@/components/TeacherProfileSheet";
import ModeToggle from "@/components/formative/ModeToggle";
import FormativePanel from "@/components/formative/FormativePanel";
import useTeacherProfile, { isProfileSet } from "@/hooks/useTeacherProfile";
import {
  SUBJECT_TAGS as FALLBACK_SUBJECT_TAGS,
  GENERAL_TAGS as FALLBACK_GENERAL_TAGS,
  GRADE_GROUPS,
  GRADE_LEVELS,
  SUBJECTS,
} from "@/data/tags";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SUBJECT = "matematika";
const DEFAULT_GRADE = "4";

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);

const subjectLabel = (slug) =>
  SUBJECTS.find((s) => s.value === slug)?.label || slug;

const gradeLabel = (val) =>
  GRADE_LEVELS.find((g) => g.value === val)?.label || val;

export default function Tracker() {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [gradeLevel, setGradeLevel] = useState(DEFAULT_GRADE);
  const [studentName, setStudentName] = useState("");

  const [availableSubjects, setAvailableSubjects] = useState(SUBJECTS);
  const [subjectTags, setSubjectTags] = useState(FALLBACK_SUBJECT_TAGS);
  const [generalTags, setGeneralTags] = useState(FALLBACK_GENERAL_TAGS);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [languageLevel, setLanguageLevel] = useState("prvi");
  const [levels, setLevels] = useState([]);
  const [subjectsGrade, setSubjectsGrade] = useState(null);
  const [mode, setMode] = useState("opisno");
  const formativeAvailable = !!availableSubjects.find((s) => (s.slug || s.value) === subject)?.formative;

  useEffect(() => {
    if (!formativeAvailable) setMode("opisno");
  }, [formativeAvailable]);

  useEffect(() => {
    setLanguageLevel("prvi");
  }, [subject]);

  const [selectedSubject, setSelectedSubject] = useState(new Set());
  const [selectedGeneral, setSelectedGeneral] = useState(new Set());

  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const { profile, save: saveProfile, reset: resetProfile, hintDismissed, dismissHint } = useTeacherProfile();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileActive = isProfileSet(profile);

  // Fetch which subjects are available for the currently-selected grade.
  // If the selected subject is not available for this grade, auto-switch to the first one.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/subjects`, { params: { grade: gradeLevel } });
        if (!active) return;
        const list = Array.isArray(res.data?.subjects) ? res.data.subjects : [];
        if (list.length === 0) {
          setSubjectsGrade(gradeLevel);
          return;
        }
        setAvailableSubjects(list);
        setSubjectsGrade(gradeLevel);
        if (!list.find((s) => s.value === subject || s.slug === subject)) {
          const first = list[0].slug || list[0].value;
          setSubject(first);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Subjects fetch failed, using fallback.", e?.message);
        if (active) setSubjectsGrade(gradeLevel);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeLevel]);

  // Fetch subject-specific tags whenever subject or grade changes.
  // Subject selection is cleared (different tag set). General selection persists.
  useEffect(() => {
    if (subjectsGrade !== gradeLevel) return undefined;
    let active = true;
    setTagsLoading(true);
    setSelectedSubject(new Set());
    (async () => {
      try {
        const res = await axios.get(`${API}/tags`, {
          params: { subject, grade: gradeLevel, level: languageLevel },
        });
        if (!active) return;
        setLevels(Array.isArray(res.data?.levels) ? res.data.levels : []);
        if (Array.isArray(res.data?.subject_tags)) {
          setSubjectTags(res.data.subject_tags);
        }
        if (Array.isArray(res.data?.general_tags) && res.data.general_tags.length) {
          setGeneralTags(res.data.general_tags);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Tags fetch failed, using fallback.", e?.message);
      } finally {
        if (active) setTagsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [subject, gradeLevel, languageLevel, subjectsGrade]);

  const customTags = useMemo(() => profile.custom_tags?.[subject] || [], [profile, subject]);

  useEffect(() => {
    const valid = new Set([...subjectTags, ...customTags]);
    setSelectedSubject((prev) => {
      const next = new Set([...prev].filter((t) => valid.has(t)));
      return next.size === prev.size ? prev : next;
    });
  }, [subjectTags, customTags]);

  const addCustomTag = (raw) => {
    const t = raw.trim().replace(/\s+/g, " ");
    if (t.length < 3) {
      toast.error("Oznaka je prekratka.");
      return false;
    }
    if ([...subjectTags, ...customTags].some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.error("Ta oznaka već postoji.");
      return false;
    }
    saveProfile({
      ...profile,
      custom_tags: { ...(profile.custom_tags || {}), [subject]: [...customTags, t] },
    });
    toast.success(`Oznaka je spremljena za ${subjectLabel(subject)} (svi razredi).`);
    return true;
  };

  const removeCustomTag = (t) => {
    saveProfile({
      ...profile,
      custom_tags: { ...(profile.custom_tags || {}), [subject]: customTags.filter((x) => x !== t) },
    });
    setSelectedSubject((prev) => {
      const next = new Set(prev);
      next.delete(t);
      return next;
    });
  };

  const totalSelected = selectedSubject.size + selectedGeneral.size;

  const toggle = (setter) => (tag) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const clearAll = () => {
    setSelectedSubject(new Set());
    setSelectedGeneral(new Set());
    toast.success("Odabir je očišćen.");
  };

  const generate = async () => {
    if (totalSelected === 0) {
      toast.error("Odaberite barem jednu oznaku prije generiranja.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/generate`, {
        student_name: studentName || null,
        grade_level: gradeLabel(gradeLevel),
        subject: subjectLabel(subject),
        language_level: levels.length ? languageLevel : null,
        subject_tags: Array.from(selectedSubject),
        general_tags: Array.from(selectedGeneral),
        teacher_profile: profileActive ? profile : null,
      });
      setOutput(res.data?.text || "");
      toast.success("Opisno praćenje je generirano.");
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Greška pri generiranju teksta.";
      toast.error(typeof msg === "string" ? msg : "Greška pri generiranju.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!output) {
      toast.error("Nema teksta za kopiranje.");
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success("Tekst je kopiran u međuspremnik.");
    } catch {
      toast.error("Kopiranje nije uspjelo. Pokušajte ručno.");
    }
  };

  const wordCount = useMemo(
    () => (output.trim() ? output.trim().split(/\s+/).length : 0),
    [output]
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tracker-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 grid place-items-center text-white shadow-sm">
              <GraduationCap className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
                Opisko
              </h1>
              <p className="text-xs text-slate-500 -mt-0.5">
                Opisno praćenje učenika · formativno vrednovanje
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setProfileOpen(true)}
              data-testid="open-profile-btn"
              className={`rounded-lg ${
                profileActive
                  ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                  : "border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Settings2 className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">
                {profileActive ? "Vaš profil" : "Postavke učitelja"}
              </span>
              {profileActive && (
                <CheckCircle2 className="h-3.5 w-3.5 ml-1 text-blue-600" />
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-10">
        {/* First-time profile hint */}
        {!hintDismissed && !profileActive && (
          <div
            className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 md:p-6 relative"
            data-testid="profile-hint-banner"
          >
            <button
              type="button"
              onClick={dismissHint}
              data-testid="dismiss-hint-btn"
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Zatvori"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="h-10 w-10 rounded-xl bg-blue-100 grid place-items-center shrink-0">
                <Sparkles className="h-5 w-5 text-blue-600" />
              </div>
              <div className="space-y-2.5">
                <h3 className="font-heading text-base font-semibold text-slate-900">
                  Postavite svoj „glas" – tekstovi će zvučati kao vi
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  AI ne zna kako vi pišete praćenja. Otvorite{" "}
                  <strong>Postavke učitelja</strong> i ukratko mu recite kakav ton
                  volite, koju duljinu i koje riječi koristite/izbjegavate. Postavi se
                  jednom – vrijedi za svaki sljedeći tekst.
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={() => {
                      setProfileOpen(true);
                      dismissHint();
                    }}
                    data-testid="open-profile-from-hint-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Settings2 className="h-3.5 w-3.5 mr-1.5" />
                    Postavi profil (30 sekundi)
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={dismissHint}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    Možda kasnije
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Intro & controls */}
        <section className="space-y-6">
          <div className="space-y-2">
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-extrabold text-slate-900">
              {mode === "formativno"
                ? "Formativne bilješke prema ishodima"
                : "Generiraj opisno praćenje u par klikova"}
            </h2>
            <p className="text-base text-slate-600 max-w-2xl leading-relaxed">
              {mode === "formativno"
                ? "Odaberi ishod iz kurikuluma i dobij bilješku za svaku razinu ostvarenosti – spremno za e-Dnevnik, bez ocjene."
                : "Odaberi oznake koje opisuju ponašanje i napredak učenika. Tekst koji generiramo je u skladu s Pravilnikom o vrednovanju i prikladan za e-Dnevnik."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm">
            <div className="space-y-1.5">
              <Label htmlFor="student" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-slate-500" />
                Ime učenika <span className="text-slate-400 font-normal">(opcionalno)</span>
              </Label>
              <Input
                id="student"
                placeholder="npr. Ivan"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                data-testid="student-name-input"
                className="rounded-lg border-slate-200"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Razred</Label>
              <Select value={gradeLevel} onValueChange={setGradeLevel}>
                <SelectTrigger className="rounded-lg border-slate-200" data-testid="grade-select">
                  <SelectValue placeholder="Odaberi razred" />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_GROUPS.map((grp) => (
                    <SelectGroup key={grp.label}>
                      <SelectLabel className="text-xs uppercase tracking-wide text-slate-400">
                        {grp.label}
                      </SelectLabel>
                      {grp.grades.map((g) => (
                        <SelectItem key={g.value} value={g.value} data-testid={`grade-option-${g.value}`}>
                          {g.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5 text-slate-500" />
                Predmet
              </Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger className="rounded-lg border-slate-200" data-testid="subject-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableSubjects.map((s) => {
                    const val = s.slug || s.value;
                    const lbl = s.label;
                    return (
                      <SelectItem key={val} value={val} data-testid={`subject-option-${val}`}>
                        {lbl}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          {formativeAvailable && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <ModeToggle value={mode} onChange={setMode} />
              <p className="text-xs text-slate-500 max-w-md">
                {mode === "formativno"
                  ? "Bilješke za jedan ishod iz kurikuluma, za svaku razinu ostvarenosti."
                  : "Opći opis aktivnosti, odgovornosti i napretka učenika."}
              </p>
            </div>
          )}
        </section>

        {mode === "formativno" ? (
          <FormativePanel
            subject={subject}
            grade={gradeLevel}
            studentName={studentName}
            profile={profileActive ? profile : null}
          />
        ) : (
        <>
        {/* Subject-specific tags */}
        <section className="space-y-4" data-testid="subject-tags-section">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800">
                Specifično za predmet · {subjectLabel(subject)}
                <span className="ml-2 text-sm font-normal text-slate-500">
                  {" "}· {gradeLabel(gradeLevel)}
                </span>
              </h3>
              <p className="text-sm text-slate-500">
                Oznake vezane uz znanja i vještine predmeta za odabrani razred.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {levels.length > 0 && (
                <LanguageLevelToggle value={languageLevel} onChange={setLanguageLevel} />
              )}
              <Badge variant="outline" className="rounded-full border-slate-200 text-slate-600" data-testid="subject-selected-count">
                {selectedSubject.size} / {subjectTags.length + customTags.length} odabrano
              </Badge>
            </div>
          </div>
          {tagsLoading ? (
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
              data-testid="subject-tags-skeleton"
            >
              {Array.from({ length: 15 }).map((_, i) => (
                <div
                  key={i}
                  className="min-h-[3.75rem] rounded-xl bg-slate-100 animate-pulse"
                />
              ))}
            </div>
          ) : subjectTags.length === 0 ? (
            <div
              className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-4"
              data-testid="no-subject-tags"
            >
              Oznake za odabranu kombinaciju predmeta i razreda još nisu dodane.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {subjectTags.map((tag) => (
                <TagButton
                  key={tag}
                  label={tag}
                  selected={selectedSubject.has(tag)}
                  onToggle={() => toggle(setSelectedSubject)(tag)}
                  testId={`subject-tag-${slugify(tag)}`}
                />
              ))}
              {customTags.map((tag) => (
                <CustomTagButton
                  key={`custom-${tag}`}
                  label={tag}
                  selected={selectedSubject.has(tag)}
                  onToggle={() => toggle(setSelectedSubject)(tag)}
                  onRemove={() => removeCustomTag(tag)}
                  testId={`custom-tag-${slugify(tag)}`}
                />
              ))}
              <CustomTagAdder onAdd={addCustomTag} />
            </div>
          )}
        </section>

        {/* General tags */}
        <section className="space-y-4 pb-24 sm:pb-8" data-testid="general-tags-section">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800">
                Općenite oznake
              </h3>
              <p className="text-sm text-slate-500">
                Aktivnost, odgovornost, vještine, autonomija i suradnja.
              </p>
            </div>
            <Badge variant="outline" className="rounded-full border-slate-200 text-slate-600">
              {selectedGeneral.size} / {generalTags.length} odabrano
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {generalTags.map((tag) => (
              <TagButton
                key={tag}
                label={tag}
                selected={selectedGeneral.has(tag)}
                onToggle={() => toggle(setSelectedGeneral)(tag)}
                testId={`general-tag-${slugify(tag)}`}
              />
            ))}
          </div>
        </section>

        {/* Actions */}
        <section className="sticky bottom-4 z-10">
          <div className="bg-white/90 backdrop-blur-md border border-slate-200 rounded-2xl p-4 md:p-5 shadow-md flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Info className="h-4 w-4 text-slate-400" />
              <span data-testid="selected-counter">
                <strong className="text-slate-900">{totalSelected}</strong> oznaka odabrano
              </span>
              {profileActive && (
                <span
                  className="hidden sm:inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-xs text-blue-700"
                  data-testid="profile-active-indicator"
                  title="AI koristi vaš osobni profil pri generiranju"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Profil aktivan
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                onClick={clearAll}
                disabled={totalSelected === 0 || loading}
                data-testid="clear-btn"
                className="text-slate-600 hover:text-slate-900"
              >
                <Eraser className="h-4 w-4 mr-1.5" />
                Očisti odabir
              </Button>
              <Button
                onClick={generate}
                disabled={loading || totalSelected === 0}
                data-testid="generate-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-5 shadow-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Generiram…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    Generiraj opisno praćenje
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>

        {/* Output */}
        <section className="space-y-3" data-testid="output-section">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800">
                Generirani tekst
              </h3>
              <p className="text-sm text-slate-500">
                Slobodno uredi tekst prije kopiranja u e-Dnevnik.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-slate-200 text-slate-600">
                {wordCount} riječi
              </Badge>
              <Button
                variant="outline"
                onClick={generate}
                disabled={loading || totalSelected === 0}
                data-testid="regenerate-btn"
                className="rounded-xl"
              >
                <RotateCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Regeneriraj
              </Button>
              <Button
                onClick={copyToClipboard}
                disabled={!output}
                data-testid="copy-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
              >
                <Copy className="h-4 w-4 mr-1.5" />
                Kopiraj u e-Dnevnik
              </Button>
            </div>
          </div>

          <Textarea
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="Ovdje će se pojaviti generirano opisno praćenje. Možeš ga slobodno uređivati prije kopiranja."
            data-testid="output-textarea"
            className="min-h-[220px] rounded-2xl border-slate-200 bg-white text-base p-5 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent leading-relaxed"
          />
        </section>
        </>
        )}

        <footer className="pt-6 pb-12 text-center text-xs text-slate-400">
          Opisko · formativno vrednovanje · usklađeno s Pravilnikom o vrednovanju (čl. 2. i čl. 12.)
        </footer>
      </main>

      <TeacherProfileSheet
        open={profileOpen}
        onOpenChange={setProfileOpen}
        initialProfile={profile}
        onSave={saveProfile}
        onReset={resetProfile}
      />
    </div>
  );
}
